import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Loader2, Save, Upload, Trash2, ImageIcon } from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPageSettings() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imgVersion, setImgVersion] = useState(Date.now());
  const fileRef = useRef(null);

  const load = () =>
    api
      .get("/admin/login-page")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)));

  useEffect(() => {
    load();
  }, []);

  if (!data) {
    return (
      <div className="flex items-center gap-2 text-neutral-500 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  const saveText = async () => {
    setSaving(true);
    try {
      await api.put("/admin/login-page", { headline: data.headline, subtext: data.subtext });
      toast.success("Login page text saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("Image too large (max 10MB)");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("photo", file);
      await api.post("/admin/login-page/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Background updated");
      setImgVersion(Date.now());
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const removeImage = async () => {
    try {
      await api.delete("/admin/login-page/image");
      toast.success("Reverted to default background");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <p className="text-sm text-neutral-500">
        Customize the sign-in page: upload a background (e.g. this week's top promoter) and add a
        short story about them and their sales. Changes are live immediately.
      </p>

      <div className="glass-panel p-4 sm:p-5">
        <h2 className="font-display font-bold text-lg tracking-tight mb-3">Background image</h2>
        <div className="flex flex-wrap items-start gap-4">
          <div className="w-40 h-56 rounded-2xl overflow-hidden border border-white/60 bg-white/50 flex items-center justify-center">
            {data.has_image ? (
              <img
                src={`${API_BASE}/public/login-image?v=${imgVersion}`}
                alt="Login background"
                className="w-full h-full object-cover"
                data-testid="login-image-preview"
              />
            ) : (
              <div className="text-center text-neutral-400 text-xs px-3">
                <ImageIcon className="h-6 w-6 mx-auto mb-2" />
                Default übbs banner in use
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="rounded-full bg-slate-900 hover:bg-slate-700 text-white"
              data-testid="login-image-upload-button"
            >
              {uploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-1" /> Upload new background
                </>
              )}
            </Button>
            {data.has_image && (
              <Button
                variant="outline"
                onClick={removeImage}
                className="rounded-full border-red-200 text-red-600 hover:bg-red-50 block"
                data-testid="login-image-remove-button"
              >
                <Trash2 className="h-4 w-4 mr-1" /> Use default banner
              </Button>
            )}
            <p className="text-[11px] text-neutral-400 max-w-[240px]">
              Portrait images work best (e.g. 1080×1660). JPG/PNG/WebP up to 10MB.
            </p>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onPick}
              data-testid="login-image-input"
            />
          </div>
        </div>
      </div>

      <div className="glass-panel p-4 sm:p-5 space-y-4">
        <h2 className="font-display font-bold text-lg tracking-tight">Overlay text</h2>
        <div>
          <Label className="text-xs uppercase tracking-wider">Headline</Label>
          <Input
            className="mt-2"
            placeholder="e.g. Sara sold 214 units this week!"
            value={data.headline}
            onChange={(e) => setData({ ...data, headline: e.target.value })}
            data-testid="login-headline-input"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider">Story / details</Label>
          <Textarea
            className="mt-2"
            rows={3}
            placeholder="A few words about the promoter and their sales…"
            value={data.subtext}
            onChange={(e) => setData({ ...data, subtext: e.target.value })}
            data-testid="login-subtext-input"
          />
        </div>
        <Button
          onClick={saveText}
          disabled={saving}
          className="rounded-full bg-slate-900 hover:bg-slate-700 text-white"
          data-testid="login-page-save-button"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save className="h-4 w-4 mr-1" /> Save text</>)}
        </Button>
        <p className="text-[11px] text-neutral-400">
          Leave both fields empty to show the default “Field force, crystal clear.” message.
        </p>
      </div>
    </div>
  );
}
